import { Injectable, inject } from '@angular/core';
import {
  CoreActionGotoNextActivity,
  CoreActionSetMenuReloadRequired,
  CoreMobiResourceType,
  CoreRouterActionNavigateRoot,
  selectorCoreNavigateToResourceStackLastItemType
} from '@bepatient/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { FactsheetApiService } from './factsheet-api.service';
import {
  FactsheetActionLoadFactsheetFailed,
  FactsheetActionLoadFactsheetSuccess,
  FactsheetActionNextButtonFactsheet,
  FactsheetActions,
  FactsheetActionTypes
} from './factsheet.actions';

@Injectable()
export class FactsheetEffects {
  private store$ = inject<Store<any>>(Store);
  private actions$ = inject(Actions);
  private factsheetApiService = inject(FactsheetApiService);

  loadFactsheet$: Observable<FactsheetActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(FactsheetActionTypes.LOAD_FACTSHEET),
      withLatestFrom(
        this.store$.select(
          selectorCoreNavigateToResourceStackLastItemType(
            CoreMobiResourceType.FACTSHEET
          )
        ),
        (action, item) => item.href
      ),
      switchMap(href =>
        this.factsheetApiService.loadFactsheet(href).pipe(
          map(res => {
            const factsheetID = href.split('factsheets/');
            res.id = factsheetID[1];
            return new FactsheetActionLoadFactsheetSuccess(res);
          }),
          catchError(() => of(new FactsheetActionLoadFactsheetFailed()))
        )
      )
    )
  );

  nextFactsheet$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FactsheetActionTypes.NEXT_BUTTON_CLICKED),
        map((action: FactsheetActionNextButtonFactsheet) => action.payload.id),
        switchMap(id =>
          this.factsheetApiService.saveFactsheet(id).pipe(
            map(data => {
              const payload = data.payload;
              const convertResource = (type: string, href: string) => {
                switch (type) {
                  case 'activity-form':
                    return { type: CoreMobiResourceType.FORM, href };
                  case 'activity-category':
                    return { type: CoreMobiResourceType.CATEGORY, href };
                  case 'activity-factsheet':
                    return { type: CoreMobiResourceType.FACTSHEET, href };
                  default:
                    return void 0;
                }
              };
              const resource = convertResource(payload.type, payload.href);
              if (!resource) {
                this.store$.dispatch(new CoreActionSetMenuReloadRequired(true));
                this.store$.dispatch(
                  new CoreRouterActionNavigateRoot({
                    url: 'timeline'
                  })
                );
              } else {
                this.store$.dispatch(new CoreActionGotoNextActivity(resource));
              }
            })
          )
        )
      ),
    { dispatch: false }
  );
}
